import { Link } from 'react-router-dom';
import type { Permission } from '@codepass/shared';
import { useAuth } from '@/auth/AuthProvider';
import { Badge, Card, CardBody, CardHeader, CardTitle } from '@/components/ui';
import { PageHeader } from '@/components/domain';

/**
 * Omraaderne under Administration. Hvert har en 'Se' og - hvor noget kan aendres - en 'Ændr'.
 * Kortet vises kun med 'Se', og uden 'Ændr' aabner siden til at laese.
 */
const SECTIONS: ReadonlyArray<{
  to: string;
  title: string;
  description: string;
  view: Permission;
  /** Any one of them edits something on the page - otherwise the card says "Kun læse". */
  manage?: readonly Permission[];
}> = [
  {
    to: '/admin/brugere',
    title: 'Brugere',
    description: 'Brugernes rolle, status og sidste login.',
    view: 'users.view',
    manage: ['users.manage'],
  },
  {
    to: '/admin/roller',
    title: 'Roller og rettigheder',
    description: 'Hvad hver rolle må se og gøre, og hvilke rapporter den har adgang til.',
    view: 'roles.view',
    manage: ['roles.manage'],
  },
  {
    to: '/admin/integrationer',
    title: 'Integrationer',
    description: 'Drivere, synkroniseringsstatus og seneste fejl pr. kildesystem.',
    view: 'integrations.view',
    manage: ['integrations.manage'],
  },
  {
    to: '/admin/regler',
    title: 'Regler',
    description: 'Detekteringsreglerne: hvornår de kører, deres tekst og tærskler.',
    view: 'rules.view',
    /* 'Ændr opbevaring af audit-log' edits the Auditoprydning rule on the same page. */
    manage: ['rules.manage', 'audit.retention'],
  },
  {
    to: '/admin/tv-dashboard',
    title: 'TV-dashboard',
    description:
      'Justér hvordan storskærmen på /dashboard/tv ser ud, fx hvor meget den er zoomet ind.',
    view: 'tv_dashboard.view',
    manage: ['tv_dashboard.manage'],
  },
  {
    to: '/admin/audit',
    title: 'Audit log',
    description: 'Søg i systemhandlinger på bruger, handling, entitet og dato.',
    view: 'audit.view',
  },
];

export function AdminHubPage() {
  const { can, canAny } = useAuth();
  const visible = SECTIONS.filter((section) => can(section.view));

  return (
    <>
      <PageHeader title="Administration" />
      <div className="grid gap-4 sm:grid-cols-2">
        {visible.map((section) => (
          <Link key={section.to} to={section.to}>
            <Card className="h-full transition-colors hover:bg-canvas">
              <CardHeader className="flex items-center justify-between gap-2">
                <CardTitle>{section.title}</CardTitle>
                {section.manage && !canAny(...section.manage) ? (
                  <Badge tone="neutral">Kun læse</Badge>
                ) : null}
              </CardHeader>
              <CardBody>
                <p className="text-xs text-ink-muted">{section.description}</p>
              </CardBody>
            </Card>
          </Link>
        ))}
      </div>
    </>
  );
}
